import React, { useEffect, useRef, useState } from 'react';
import axios from '../../config/axiosInstance';
import { Card, Typography, Spin, Button, Select, message } from 'antd';
import { BASE_URL } from '../../config';

const { Title } = Typography;
const { Option } = Select;

const ConsoleLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshCountdown, setRefreshCountdown] = useState(30);
    const [isPaused, setIsPaused] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState('all');
    const terminalRef = useRef(null);

    const fetchLogs = async (level = selectedLevel) => {
        try {
            setLoading(true);

            //If "ws" was selected, first get active connections
            if (level === 'ws') {
                await axios.get('/ws/stats');
            }

            const endpoint = level === 'all'
                ? '/console-logs'
                : `/console-logs?level=${level}`;

            const res = await axios.get(endpoint);
            setLogs(res.data);
            setRefreshCountdown(30);

            // Scroll to the bottom
            setTimeout(() => {
                if (terminalRef.current) {
                    terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
                }
            }, 50);
        } catch (error) {
            console.error('❌ Error fetching logs:', error.message);
            message.error('Failed to load logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isPaused) {
            fetchLogs();
        }

        const intervalLogs = setInterval(() => {
            if (!isPaused) {
                fetchLogs();
            }
        }, 30000);

        const intervalCountdown = setInterval(() => {
            if (!isPaused) {
                setRefreshCountdown(prev => (prev > 0 ? prev - 1 : 30));
            }
        }, 1000);

        return () => {
            clearInterval(intervalLogs);
            clearInterval(intervalCountdown);
        };
    }, [isPaused, selectedLevel]);

    const handleLevelChange = (value) => {
        setSelectedLevel(value);
        fetchLogs(value); // Call immediately when changing
    };

    return (
        <Card style={{ margin: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={3}>🖥️ Console Logs</Title>
                <Select
                    size="small"
                    value={selectedLevel}
                    onChange={handleLevelChange}
                    style={{ width: 140 }}
                >
                    <Option value="all">🔎 All</Option>
                    <Option value="info">ℹ️ Info</Option>
                    <Option value="warn">⚠️ Warn</Option>
                    <Option value="error">❌ Error</Option>
                    <Option value="ws">📡 WebSocket</Option>
                </Select>
            </div>

            {/* Terminal */}
            <div style={{
                backgroundColor: '#1e1e1e',
                color: '#fff',
                borderRadius: 8,
                fontFamily: 'monospace',
                height: '70vh',
                boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                border: '1px solid #333',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* Top bar */}
                <div style={{
                    height: 32,
                    backgroundColor: '#2d2d2d',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                }}>
                    <div style={{ position: 'absolute', left: 12, display: 'flex', gap: 8 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: '#ff5f56', borderRadius: '50%' }} />
                        <div style={{ width: 12, height: 12, backgroundColor: '#ffbd2e', borderRadius: '50%' }} />
                        <div style={{ width: 12, height: 12, backgroundColor: '#27c93f', borderRadius: '50%' }} />
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 12,
                        color: '#ccc',
                        gap: 8
                    }}>
                        <img
                            src="/default_appicon.svg"
                            alt="Logo"
                            style={{ height: 16, width: 16, marginRight: 4 }}
                        />
                        <span style={{ fontWeight: 'bold' }}>dsync-backend</span>
                        <span style={{ color: '#888' }}>→ {BASE_URL || 'BASE_URL not set'}</span>
                    </div>

                    {/* Top right: countdown + pause */}
                    <div style={{
                        position: 'absolute',
                        right: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        <span style={{
                            fontSize: 12,
                            color: isPaused ? 'red' : '#aaa',
                            fontWeight: isPaused ? 'bold' : 'normal'
                        }}>
                            {isPaused
                                ? '🔴 Refresh: PAUSED'
                                : `🔄 Refresh in: ${refreshCountdown}s`}
                        </span>
                        <Button
                            size="small"
                            onClick={() => setIsPaused(prev => !prev)}
                            style={{
                                padding: '0 8px',
                                fontSize: 12,
                                height: 24,
                                backgroundColor: '#3a3a3a',
                                color: '#ccc',
                                border: '1px solid #444',
                                borderRadius: 4,
                            }}
                        >
                            {isPaused ? '▶️' : '⏸️'}
                        </Button>
                    </div>
                </div>

                {/* Scrollable content */}
                <div
                    ref={terminalRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '12px 16px',
                    }}
                >
                    {loading ? (
                        <Spin />
                    ) : (
                        logs.map((log, index) => (
                            <div key={index} style={{ marginBottom: 4 }}>
                                <span dangerouslySetInnerHTML={{ __html: log.message }} />
                            </div>
                        ))
                    )}
                </div>
            </div>
        </Card>
    );
};

export default ConsoleLogs;